import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import CustomModal from "@/components/ui/CustomModal.web";
import PasswordInput from "@/components/ui/PasswordInput";
import { PhoneAuthFlow } from "@/components/ui/PhoneAuthFlow.web";
import { useAuth } from "@/context/AuthContext";
import {
  AccountLinkingError,
  getLinkedProviders,
  linkEmailProvider,
  linkGoogleProvider,
  unlinkProvider,
} from "@/utils/accountLinking.web";
import { maskEmail, maskPhone } from "@/utils/maskIdentifier";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import Head from "expo-router/head";
import type { User } from "firebase/auth";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, StyleSheet, TextInput, View } from "react-native";

type ProviderKey = "password" | "google.com" | "phone";

const PROVIDERS: {
  key: ProviderKey;
  labelKey: string;
  icon: React.ComponentProps<typeof Ionicons>["name"];
}[] = [
  { key: "password", labelKey: "EMAIL", icon: "mail-outline" },
  { key: "google.com", labelKey: "GOOGLE", icon: "logo-google" },
  { key: "phone", labelKey: "PHONE_NUMBER", icon: "call-outline" },
];

const providerDisplayLabel = (
  user: User | null,
  key: ProviderKey,
): string | null => {
  if (!user) return null;
  const data = user.providerData.find((p) => p.providerId === key);
  if (!data) return null;
  if (key === "password") {
    const raw = data.email || user.email || null;
    return raw ? maskEmail(raw) : null;
  }
  if (key === "google.com") {
    return data.email ? maskEmail(data.email) : null;
  }
  if (key === "phone") {
    const raw = data.phoneNumber || user.phoneNumber || null;
    return raw ? maskPhone(raw) : null;
  }
  return null;
};

const SignInMethodsPage = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const webUser = user as User | null;

  const [providers, setProviders] = useState<string[]>(() =>
    webUser ? getLinkedProviders(webUser) : [],
  );
  const [busyProvider, setBusyProvider] = useState<ProviderKey | null>(null);
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
    onOk?: () => void;
  } | null>(null);

  const [linkEmailOpen, setLinkEmailOpen] = useState(false);
  const [linkPhoneOpen, setLinkPhoneOpen] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<ProviderKey | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const primaryProviderId = useMemo(
    () => webUser?.providerData[0]?.providerId ?? null,
    [webUser],
  );

  const refreshProviders = () => {
    if (webUser) {
      setProviders(getLinkedProviders(webUser));
    }
  };

  const showLinkError = (
    err: unknown,
    fallbackTitleKey: string,
    fallbackMessageKey: string,
  ) => {
    if (err instanceof AccountLinkingError) {
      switch (err.code) {
        case "USER_CANCELLED":
          return;
        case "CREDENTIAL_ALREADY_IN_USE":
          setModalInfo({
            title: t("CREDENTIAL_ALREADY_IN_USE_TITLE"),
            message: t("CREDENTIAL_ALREADY_IN_USE_MESSAGE"),
          });
          return;
        case "PROVIDER_ALREADY_LINKED":
          setModalInfo({
            title: t("ERROR"),
            message: t("PROVIDER_ALREADY_LINKED_MESSAGE"),
          });
          return;
        case "REQUIRES_RECENT_LOGIN":
          setModalInfo({
            title: t("REQUIRES_RECENT_LOGIN_TITLE"),
            message: t("REQUIRES_RECENT_LOGIN_MESSAGE"),
            onOk: () => {
              setModalInfo(null);
              logout();
            },
          });
          return;
        case "POPUP_BLOCKED":
          setModalInfo({
            title: t("ERROR"),
            message: t("POPUP_BLOCKED_MESSAGE"),
          });
          return;
      }
    }
    setModalInfo({
      title: t(fallbackTitleKey),
      message: t(fallbackMessageKey),
    });
  };

  const handleLinkGoogle = async () => {
    if (!webUser) return;
    setBusyProvider("google.com");
    try {
      await linkGoogleProvider(webUser);
      refreshProviders();
      setModalInfo({
        title: t("SUCCESS"),
        message: t("GOOGLE_LINK_SUCCESS"),
      });
    } catch (err) {
      showLinkError(err, "ERROR", "GOOGLE_LINK_ERROR");
    } finally {
      setBusyProvider(null);
    }
  };

  const openLinkEmail = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setEmailError(null);
    setLinkEmailOpen(true);
  };

  const handleSubmitLinkEmail = async () => {
    if (!webUser) return;
    setEmailError(null);
    if (!email || !email.includes("@")) {
      setEmailError(t("PLEASE_ENTER_A_VALID_EMAIL"));
      return;
    }
    if (password.length < 6) {
      setEmailError(t("PASSWORD_MIN_LENGTH_ERROR"));
      return;
    }
    if (password !== confirmPassword) {
      setEmailError(t("PASSWORDS_DO_NOT_MATCH"));
      return;
    }

    setBusyProvider("password");
    try {
      await linkEmailProvider(webUser, email, password);
      refreshProviders();
      setLinkEmailOpen(false);
      setModalInfo({
        title: t("SUCCESS"),
        message: t("EMAIL_LINK_SUCCESS"),
      });
    } catch (err) {
      showLinkError(err, "ERROR", "EMAIL_LINK_ERROR");
    } finally {
      setBusyProvider(null);
    }
  };

  const openLinkPhone = () => {
    setLinkPhoneOpen(true);
  };

  const handlePhoneLinkSuccess = async () => {
    refreshProviders();
    setLinkPhoneOpen(false);
    setModalInfo({
      title: t("SUCCESS"),
      message: t("PHONE_LINK_SUCCESS"),
    });
  };

  const handlePhoneSendError = () => {
    setModalInfo({
      title: t("ERROR"),
      message: t("PHONE_LINK_ERROR"),
    });
  };

  const requestUnlink = (key: ProviderKey) => {
    setUnlinkTarget(key);
  };

  const confirmUnlink = async () => {
    if (!webUser || !unlinkTarget) return;
    const target = unlinkTarget;
    setUnlinkTarget(null);
    setBusyProvider(target);
    try {
      await unlinkProvider(webUser, target);
      refreshProviders();
      setModalInfo({
        title: t("SUCCESS"),
        message: t("UNLINK_SUCCESS", { provider: t(labelKeyFor(target)) }),
      });
    } catch (err) {
      if (
        err instanceof AccountLinkingError &&
        err.code === "ONLY_LINKED_PROVIDER"
      ) {
        setModalInfo({
          title: t("ERROR"),
          message: t("UNLINK_ONLY_PROVIDER_HELPER"),
        });
      } else {
        showLinkError(err, "ERROR", "UNLINK_ERROR");
      }
    } finally {
      setBusyProvider(null);
    }
  };

  const labelKeyFor = (key: ProviderKey) => {
    return PROVIDERS.find((p) => p.key === key)?.labelKey || "";
  };

  return (
    <>
      <Head>
        <title>{t("PAGE_TITLE_SETTINGS_SIGNIN")}</title>
      </Head>
      <CustomHeader
        title={t("SIGN_IN_METHODS")}
        type="subtitle"
        variant="tab"
        showBackButton
        onBackPress={() => router.replace("/(tabs)/settings")}
      />
      <ThemedView style={styles.container}>
        <ThemedText style={styles.subtitle}>
          {t("SIGN_IN_METHODS_SUBTITLE")}
        </ThemedText>

        {PROVIDERS.map((row) => {
          const isLinked = providers.includes(row.key);
          const display = providerDisplayLabel(webUser, row.key);
          const isPrimary = primaryProviderId === row.key;
          const isOnly = providers.length <= 1;
          const isBusy = busyProvider === row.key;
          const rowTestId = `signin-method-row-${
            row.key === "google.com" ? "google" : row.key
          }`;
          return (
            <ThemedView
              key={row.key}
              testID={rowTestId}
              style={styles.row}
            >
              <ThemedView style={styles.rowLeft}>
                <Ionicons name={row.icon} size={24} />
                <ThemedView style={styles.rowText}>
                  <ThemedText style={styles.rowTitle}>
                    {t(row.labelKey)}
                    {isPrimary && isLinked ? (
                      <ThemedText style={styles.primaryTag}>
                        {"  "}
                        {t("PRIMARY_PROVIDER_LABEL")}
                      </ThemedText>
                    ) : null}
                  </ThemedText>
                  <ThemedText style={styles.rowSub}>
                    {isLinked ? display || t("LINKED") : t("NOT_LINKED")}
                  </ThemedText>
                  {isLinked && isOnly ? (
                    <ThemedText style={styles.helperText}>
                      {t("UNLINK_ONLY_PROVIDER_HELPER")}
                    </ThemedText>
                  ) : null}
                </ThemedView>
              </ThemedView>
              <ThemedView style={styles.rowAction}>
                {isBusy ? (
                  <ActivityIndicator />
                ) : isLinked ? (
                  isOnly ? null : (
                    <Button
                      text={t("UNLINK")}
                      onPress={() => requestUnlink(row.key)}
                      style={[styles.actionBtn, styles.unlinkBtn]}
                      textStyle={styles.unlinkBtnText}
                    />
                  )
                ) : (
                  <Button
                    text={t("LINK")}
                    onPress={() => {
                      if (row.key === "google.com") handleLinkGoogle();
                      else if (row.key === "password") openLinkEmail();
                      else if (row.key === "phone") openLinkPhone();
                    }}
                    style={styles.actionBtn}
                  />
                )}
              </ThemedView>
            </ThemedView>
          );
        })}
      </ThemedView>

      {/* Link email/password modal */}
      {linkEmailOpen && (
        <CustomModal
          visible={linkEmailOpen}
          onClose={() => setLinkEmailOpen(false)}
          title={t("LINK_EMAIL_PASSWORD")}
        >
          <ThemedText style={styles.modalDescription}>
            {t("LINK_EMAIL_PASSWORD_DESCRIPTION")}
          </ThemedText>
          <TextInput
            style={styles.textInput}
            value={email}
            onChangeText={setEmail}
            placeholder={t("EMAIL")}
            placeholderTextColor="#888"
            autoCapitalize="none"
            keyboardType="email-address"
            autoComplete="email"
          />
          <PasswordInput
            value={password}
            onChangeText={setPassword}
            autoComplete="new-password"
            containerStyle={styles.modalPasswordInput}
          />
          <PasswordInput
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            placeholder={t("CONFIRM_PASSWORD")}
            autoComplete="new-password"
            containerStyle={styles.modalPasswordInput}
          />
          {emailError ? (
            <ThemedText style={styles.errorText}>{emailError}</ThemedText>
          ) : null}
          <Button
            text={
              busyProvider === "password" ? t("LINKING") : t("LINK_ACCOUNT")
            }
            onPress={handleSubmitLinkEmail}
            disabled={busyProvider === "password"}
            style={{ marginTop: 12, width: "100%" }}
          />
        </CustomModal>
      )}

      {/* Link phone modal */}
      {linkPhoneOpen && (
        <CustomModal
          visible={linkPhoneOpen}
          onClose={() => setLinkPhoneOpen(false)}
          title={t("LINK_PHONE_TITLE")}
        >
          <PhoneAuthFlow
            mode="link"
            user={webUser}
            onSuccess={handlePhoneLinkSuccess}
            onCancel={() => setLinkPhoneOpen(false)}
            onSendError={handlePhoneSendError}
            subtitleKey="LINK_PHONE_DESCRIPTION"
            sendCodeKey="PHONE_SIGNUP_SEND_CODE"
            loading={phoneLoading}
            onLoadingChange={setPhoneLoading}
          />
        </CustomModal>
      )}

      {/* Unlink confirmation modal */}
      {unlinkTarget && (
        <CustomModal
          visible={!!unlinkTarget}
          onClose={() => setUnlinkTarget(null)}
          title={t("UNLINK_PROVIDER_TITLE", {
            provider: t(labelKeyFor(unlinkTarget)),
          })}
        >
          <ThemedText style={styles.modalDescription}>
            {t("UNLINK_PROVIDER_MESSAGE", {
              provider: t(labelKeyFor(unlinkTarget)),
            })}
          </ThemedText>
          <View style={styles.modalActions}>
            <Button
              text={t("BACK")}
              onPress={() => setUnlinkTarget(null)}
              style={styles.modalCancelBtn}
              textStyle={{ color: "#333" }}
              testID="unlink-modal-cancel"
            />
            <Button
              text={t("UNLINK")}
              onPress={confirmUnlink}
              style={styles.modalDangerBtn}
              testID="unlink-modal-confirm"
            />
          </View>
        </CustomModal>
      )}

      {/* Info/error modal */}
      {modalInfo && (
        <CustomModal
          visible={!!modalInfo}
          onClose={() => {
            const cb = modalInfo?.onOk;
            setModalInfo(null);
            if (cb) cb();
          }}
          title={modalInfo.title}
        >
          <ThemedText>{modalInfo.message}</ThemedText>
          <Button
            text={t("OK")}
            onPress={() => {
              const cb = modalInfo?.onOk;
              setModalInfo(null);
              if (cb) cb();
            }}
            style={{ marginTop: 16 }}
          />
        </CustomModal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignSelf: "center",
    width: "100%",
    maxWidth: 600,
  },
  subtitle: {
    fontSize: 14,
    opacity: 0.75,
    marginBottom: 20,
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingVertical: 16,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  rowLeft: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    flex: 1,
  },
  rowText: {
    flex: 1,
  },
  rowTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  primaryTag: {
    fontSize: 12,
    fontWeight: "500",
    color: "#0a7ea4",
  },
  rowSub: {
    fontSize: 13,
    opacity: 0.7,
    marginTop: 2,
  },
  helperText: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 4,
  },
  rowAction: {
    minWidth: 90,
    alignItems: "flex-end",
  },
  actionBtn: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  unlinkBtn: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#dc2626",
  },
  unlinkBtnText: {
    color: "#dc2626",
  },
  modalDescription: {
    fontSize: 14,
    marginBottom: 12,
  },
  textInput: {
    height: 44,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    backgroundColor: "#fafafa",
    marginBottom: 12,
    width: "100%",
  },
  modalPasswordInput: {
    maxWidth: "100%",
  },
  errorText: {
    color: "#dc2626",
    fontSize: 13,
    marginTop: 4,
  },
  modalActions: {
    flexDirection: "row",
    gap: 10,
    marginTop: 16,
  },
  modalCancelBtn: {
    flex: 1,
    backgroundColor: "#e5e7eb",
  },
  modalDangerBtn: {
    flex: 1,
    backgroundColor: "#dc2626",
  },
});

export default SignInMethodsPage;
